import type { ReactNode } from 'react';

import { Skeleton } from '~/components/ui/skeleton';

import { useProductCategoryDisplay } from '../hooks/use-product-category-display';

interface ProductCategoryProps {
  /** Raw category from the product — a numeric GPC id or an already-readable path. */
  value: string | number | null | undefined;
  /** Presenter for the resolved/passthrough string; the caller supplies its own
   *  styling (cell, path, fact) so each surface keeps its look. */
  children: (display: string) => ReactNode;
  /** Automation hook: the loading skeleton gets `${testId}-loading`. */
  testId?: string;
}

/**
 * Renders a product category, resolving a numeric GPC id to its en-US name first
 * (a dash when unresolvable) and passing text paths through untouched. Shows a
 * skeleton only while a numeric id is being fetched.
 */
export function ProductCategory({ value, children, testId }: ProductCategoryProps) {
  const { display, isResolving } = useProductCategoryDisplay(value);
  if (isResolving)
    return <Skeleton data-testid={testId ? `${testId}-loading` : undefined} className="h-4 w-16" />;
  return <>{children(display)}</>;
}
